<script lang="ts" setup>
const props = defineProps({
  company: {
    default: undefined,
    type: Object as PropType<CompanyParent | CompanyV2 | undefined>,
  },
  isClickable: {
    default: false,
    type: Boolean,
  },
  showDescription: {
    default: true,
    type: Boolean,
  },
})

const emit = defineEmits<{
  avatarClicked: [domain: string]
}>()

const { t } = useI18n()
const { checkAvailableCredits } = useSubscription()

const description = computed(() => {
  if (!props.showDescription) {
    return ''
  }

  return getCompanyDescription(props.company)
})

const domain = computed(() => {
  return getCompanyDomain(props.company)
})

const isRedirection = computed(() => {
  return isCompanyRedirection(props.company)
})

const name = computed(() => {
  return getCompanyName(props.company)
})

const socialNetworks = computed(() => {
  if (!props.company || !('socials' in props.company)) {
    return {}
  }

  return props.company.socials || {}
})

const urls = computed(() => {
  if (!props.company || !('urls' in props.company)) {
    return {}
  }

  return props.company.urls || {}
})

function onAvatarClicked() {
  if (props.isClickable && props.company && checkAvailableCredits()) {
    emit('avatarClicked', domain.value)
  }
}
</script>

<template>
  <div class="flex w-full flex-col items-center justify-center gap-4 text-left">
    <div class="flex w-[280px] max-w-[280px] items-center justify-center sm:w-full sm:max-w-full">
      <UAvatar
        class="mr-4 flex-initial"
        :class="{
          'cursor-pointer': isClickable,
          'pointer-events-none': !isClickable,
        }"
        :icon="getIcon('companies')"
        size="xl"
        :src="getLogo(domain, 'companies')"
        @click="onAvatarClicked"
      />

      <div class="flex flex-col overflow-hidden">
        <span
          v-if="name"
          class="truncate uppercase"
        >
          {{ name }}
        </span>

        <ULink
          v-if="domain"
          class="text-sm text-indigo-600 underline dark:text-indigo-400"
          target="_blank"
          :to="`http://${domain}`"
        >
          {{ domain }}
        </ULink>
      </div>
    </div>

    <div
      v-if="Object.keys(socialNetworks).length || description || isRedirection"
      class="shadow-xs dark:border-gray-8M00 flex w-full flex-col items-center gap-2.5 rounded-md border border-gray-200 bg-gray-100 p-4 text-center text-sm dark:border-gray-800 dark:bg-gray-900"
    >
      <CompanySocials
        v-if="Object.keys(socialNetworks).length"
        has-tooltip
        :socials="socialNetworks"
        :urls="urls"
      />

      <p v-if="description">
        {{ description || '&nbsp;' }}
      </p>

      <p
        v-if="isRedirection"
        class="flex flex-col items-center justify-center"
      >
        {{ t('company.redirection') }}

        <ULink
          class="text-gray-700 underline dark:text-gray-300"
          target="_blank"
          :to="`http://${(company as CompanyV2).domain?.redirection}`"
        >
          {{ (company as CompanyV2).domain?.redirection }}
        </ULink>
      </p>

      <slot />
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "company": {
      "label": "LinkedIn",
      "redirection": "This company redirects to another company",
      "tooltip": "Show LinkedIn profile"
    },
    "employees": {
      "label": "Employees",
      "tooltip": "Show employees on LinkedIn"
    },
    "salesNavigator": {
      "label": "Sales Navigator",
      "tooltip": "Show Sales Navigator Profile"
    }
  },
  "es": {
    "company": {
      "label": "LinkedIn",
      "redirection": "Esta empresa redirige a otra empresa",
      "tooltip": "Mostrar perfil de LinkedIn"
    },
    "employees": {
      "label": "Empleados",
      "tooltip": "Mostrar empleados en LinkedIn"
    },
    "salesNavigator": {
      "label": "Sales Navigator",
      "tooltip": "Mostrar perfil de Sales Navigator"
    }
  },
  "fr": {
    "company": {
      "label": "LinkedIn",
      "redirection": "Cette entreprise redirige vers une autre entreprise",
      "tooltip": "Voir le profil LinkedIn"
    },
    "employees": {
      "label": "Employés",
      "tooltip": "Voir les employés sur LinkedIn"
    },
    "salesNavigator": {
      "label": "Sales Navigator",
      "tooltip": "Voir le profil Sales Navigator"
    }
  }
}
</i18n>
